import { z } from "zod";
import { namesIn, SourceRef } from "./article.js";
import { CHANNEL_PLATFORMS, PLATFORMS, refusedBecause, type ChannelPlatform } from "./channels.js";

/**
 * The post drafter ([31](../../../docs/31-posts-joogoos-drafts.md) §the drafter, ADR 0056): posts written from what the
 * business already says, as drafts an owner approves, and nothing more.
 *
 * **A model proposes, code decides what is kept.** `draft_posts` is one paid call for a batch — a few ideas, each
 * with one post per network — answered in the closed shape below. Then `checkDrafts` reads every post the way the
 * drafts route reads one a person types: the network's own length, counted its own way, and every name the business
 * never names, which the drafter is not told (as the article writer is not: a list of rivals handed to a writer is a
 * list of rivals to write about). A post that fails is dropped, not shown with a warning; so is one that cites a
 * source it was not given, or says again what the business already said or turned down. What code cannot check —
 * whether a sentence overclaims — is put in front of the owner instead: every draft keeps the source it drew on.
 *
 * Nothing here approves, schedules or publishes. The rows it leads to are `scheduled_posts` at `draft`, and only an
 * owner's press moves one (ADR 0056 point 2).
 */

/**
 * The networks the drafter writes for: the ones that take words alone. A video-only network (TikTok, YouTube) and one
 * that needs a picture (Instagram) get nothing from it, because a drafted post has neither until somebody makes one.
 * Read from `PLATFORMS`, so a network that starts taking words alone is drafted for without a change here.
 */
export const DRAFT_PLATFORMS: readonly ChannelPlatform[] = CHANNEL_PLATFORMS.filter((p) => PLATFORMS[p].textAlone);

/** At most this many ideas in one batch. The preparer asks for fewer; this bounds a model that answers more. */
export const DRAFT_IDEAS_MAX = 5;

/** What the drafter is given: the business in its own words, its rules, and what it already said. Never its rivals. */
export const DraftPostsInput = z.object({
  /** The business's name, as its page says it. */
  business: z.string().max(200).default(""),
  /**
   * Everything a post may draw on, numbered as the articles number it: the business page's sections (`B:what`,
   * `B:who`, …) and the sources it keeps (`S1`, `S2`, …). Each idea cites one, and code drops one that cites anything
   * else. `rivals` is never among them.
   */
  sources: z.array(z.object({ ref: SourceRef, title: z.string().max(200), text: z.string().max(4_000) })).max(30),
  /** *How we sound*, from the business page: a tone, not a source to cite. */
  tone: z.string().max(1_000).default(""),
  /** The networks this business can post to, of `DRAFT_PLATFORMS`: one post per network per idea. */
  platforms: z.array(z.enum(CHANNEL_PLATFORMS)).min(1).max(CHANNEL_PLATFORMS.length),
  /** Subjects to stay off and claims never to make, as the articles are given them (21 §1). */
  neverTopic: z.array(z.string().max(200)).max(40).default([]),
  neverClaim: z.array(z.string().max(200)).max(40).default([]),
  /**
   * The last month's posts — waiting, turned down, and gone out — so it neither says the same thing twice nor proposes
   * again what the owner said no to. Code checks the first again; only the owner can check the second.
   */
  recent: z.array(z.object({
    platform: z.enum(CHANNEL_PLATFORMS),
    text: z.string().max(3_000),
    said: z.enum(["waiting", "rejected", "out"]),
  })).max(60).default([]),
  /**
   * What the owner said yes to in their marketing review this week, when it was about posts (ADR 0064 point 5): the
   * ideas serve it. Empty when there is no such yes.
   */
  focus: z.string().max(600).default(""),
  /** How many ideas to propose. */
  most: z.number().int().min(1).max(DRAFT_IDEAS_MAX).default(3),
});
export type DraftPostsInput = z.infer<typeof DraftPostsInput>;

/**
 * What it answers. Deliberately looser than what is kept — the source is any string and the network any network —
 * because a paid answer that names a source it was not given should cost one idea, not the whole batch.
 */
export const DraftPostsOutput = z.object({
  ideas: z.array(z.object({
    /** The idea in a line, for whoever reads the call later. Not shown as a post. */
    idea: z.string().max(400),
    /** The ref of the one source it draws on, exactly as it was given. */
    source: z.string().max(40),
    posts: z.array(z.object({ platform: z.enum(CHANNEL_PLATFORMS), text: z.string().max(4_000) })).max(CHANNEL_PLATFORMS.length),
  })).max(DRAFT_IDEAS_MAX),
});
export type DraftPostsOutput = z.infer<typeof DraftPostsOutput>;

/**
 * Whether this business would put its name to these words, or why not: the network's own limits, then the names it
 * never mentions. One function for the drafts route, every edit, every approval and the drafter, so a post the
 * drafter keeps is one a person could have saved, and an edit cannot bring back a name a draft was refused for.
 */
export function postRefusal(
  platform: ChannelPlatform, caption: string, media: Omit<Parameters<typeof refusedBecause>[1], "caption">, neverName: readonly string[],
): string | null {
  const no = refusedBecause(platform, { ...media, caption });
  if (no) return no;
  const named = namesIn(caption, [...neverName]);
  return named.length ? `This names ${named.join(", ")}, which this business never names.` : null;
}

/** A post the drafter wrote that code kept. `source` is the title of what it drew on, as the owner reads it. */
export interface KeptDraft {
  platform: ChannelPlatform;
  caption: string;
  ref: string;
  source: string;
  idea: string;
  /** Which idea of the batch, from 0, so the posts of one idea can be planned for the same day. */
  index: number;
}

/** The same words, however they are spaced or capitalised: what "says the same thing again" is held to by code. */
export const sameWords = (s: string) => s.normalize("NFC").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * What survives a batch. Never throws and never asks a model anything: given the answer and the business's own
 * settings it returns the posts to keep and, for the log, why each other one was dropped.
 */
export function checkDrafts(out: DraftPostsOutput, ctx: {
  platforms: readonly ChannelPlatform[];
  sources: ReadonlyArray<{ ref: string; title: string }>;
  neverName: readonly string[];
  /** Everything posted, waiting or turned down lately, as `recent` gave it. */
  recent: readonly string[];
  most: number;
}): { kept: KeptDraft[]; dropped: string[] } {
  const titles = new Map(ctx.sources.map((s) => [s.ref, s.title]));
  const seen = new Set(ctx.recent.map(sameWords));
  const kept: KeptDraft[] = [];
  const dropped: string[] = [];
  out.ideas.slice(0, ctx.most).forEach((idea, index) => {
    const source = titles.get(idea.source.trim());
    if (!source) {
      dropped.push(`idea ${index + 1}: cites ${idea.source || "nothing"}, which it was not given`);
      return;
    }
    const done = new Set<ChannelPlatform>();
    for (const post of idea.posts) {
      const caption = post.text.trim();
      const where = `idea ${index + 1}, ${PLATFORMS[post.platform].label}`;
      if (!ctx.platforms.includes(post.platform)) { dropped.push(`${where}: not a network this business posts to`); continue; }
      if (done.has(post.platform)) { dropped.push(`${where}: a second post for the same network`); continue; }
      if (!caption) { dropped.push(`${where}: empty`); continue; }
      const no = postRefusal(post.platform, caption, { hasMedia: false, isVideo: false }, ctx.neverName);
      if (no) { dropped.push(`${where}: ${no}`); continue; }
      if (seen.has(sameWords(caption))) { dropped.push(`${where}: says again what was already said`); continue; }
      seen.add(sameWords(caption));
      done.add(post.platform);
      kept.push({ platform: post.platform, caption, ref: idea.source.trim(), source, idea: idea.idea.trim(), index });
    }
  });
  return { kept, dropped };
}

/** An address on the business page's *Where we are online* that says the business is on this network. */
const ON: Partial<Record<ChannelPlatform, RegExp>> = {
  x: /(?:^|[/\s.])(?:x|twitter)\.com\/@?[A-Za-z0-9_]{1,15}\b/i,
  linkedin: /linkedin\.com\/(?:company|in|showcase)\//i,
  facebook: /facebook\.com\/[^\s?#/]+/i,
};

/**
 * The networks a business can post to, of those the drafter writes for: connected through Joogoos, or named on its
 * business page where it says where it is online (31 §the queue) — where a post goes up by hand. A network it is on
 * neither way is one it has not said it uses, and a draft for it would be a draft for nobody.
 */
export function draftNetworks(connected: readonly ChannelPlatform[], links: string): ChannelPlatform[] {
  return DRAFT_PLATFORMS.filter((p) => connected.includes(p) || Boolean(ON[p]?.test(links)));
}
